import { useCallback, useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import Select from "react-select"
import {
  Library, ClipboardList, Users, Search,
  LayoutGrid, Table as TableIcon, Loader,
  Crown, FolderPlus, FileQuestion, BookOpen,
  PlusCircle, Zap,
  GraduationCap,
  FileText
} from "lucide-react"

import { getMyCourses } from "@/api/kuizy"
import { formatDateTime } from "@/settings/master"
import CourseTestsModal from "@/widgets/CourseTestsModal"
import CourseContentModal from "@/widgets/CourseContentModal"
import ViewSwitcher from "../../components/ui/ViewSwitcher"
// (ถ้าต้องให้ผู้สอนดูรายชื่อผู้เรียน/ผู้สอนด้วย ก็สามารถ reuse CourseUsersModal ได้)
// import CourseUsersModal from "@/widgets/CourseUsersModal"

const STATUS_OPTIONS = [
  { value: "active", label: "เปิดใช้งาน" },
  { value: "inactive", label: "ปิดใช้งาน" },
  { value: "all", label: "ทั้งหมด" },
]

export default function CreatorCoursesPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoad] = useState(true)
  const [view, setView] = useState("card") // 'card' | 'table'
  const [searchIn, setSI] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState(STATUS_OPTIONS[0])

  const [testsModal, setTestsModal] = useState({ open: false, courseId: null, courseName: "" })
  // const [usersModal, setUsersModal] = useState({ open: false, courseId: null, courseName: "" })
  const [contentsModal, setContentsModal] = useState({ open: false, courseId: null, courseName: "" })

  const load = useCallback(async () => {
    setLoad(true)
    try {
      const { data } = await getMyCourses({
        search: search || undefined,
        status: status?.value || "active",
      })
      setRows(Array.isArray(data) ? data : (data?.data ?? []))
    } finally {
      setLoad(false)
    }
  }, [search, status])

  useEffect(() => { load() }, [load])

  const list = useMemo(() => rows, [rows])

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: .35 }}
      className="flex flex-col gap-6 p-4 sm:p-6"
    >
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-3 justify-between"
      >
        <h1 onClick={load} className="flex items-center gap-2 text-lg font-semibold cursor-pointer">
          <Library size={20} /> เพิ่มชุดข้อสอบเข้าหลักสูตร (ผู้สอน) {loading ? "" : `(${list.length})`}
        </h1>
        <div className="flex flex-wrap gap-2">
          <ViewSwitcher view={view} setView={setView} />
        </div>
      </motion.header>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-4 bg-white rounded-xl shadow-md p-4"
      >
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหาหลักสูตร</label>
          <div className="relative">
            <input
              value={searchIn} onChange={e => setSI(e.target.value)}
              onKeyDown={e => e.key === "Enter" && setSearch(searchIn)}
              className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary"
              placeholder="ชื่อหลักสูตร / ชื่อย่อ"
            />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>

        <div className="min-w-[200px]">
          <label className="flex items-center gap-1 text-sm"><FolderPlus size={16} /> สถานะ</label>
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            classNames={{
              control: () => 'border-gray-300 shadow-sm rounded-md min-h-[38px] text-sm',
              menu: () => 'text-sm z-40'
            }}
            placeholder="เลือกสถานะ"
          />
        </div>

        <button
          onClick={() => setSearch(searchIn)}
          className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"
        >
          <Search size={16} /> ค้นหา
        </button>
      </motion.div>

      {/* Card View */}
      {view === "card" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: .35, delay: .1 }}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <Loader className="animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="col-span-full text-center text-gray-500 py-12">
              – ไม่มีหลักสูตรที่คุณเป็นผู้สอน –
            </div>
          ) : list.map(c => (

            <motion.div
              key={c.id}
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl shadow-md p-6 flex flex-col gap-5"
            >

              {/* header */}
              <div className="flex items-start justify-between">

                <div>
                  <h4 className="font-semibold text-gray-800 line-clamp-2 min-h-[48px]">
                    {c.name}
                  </h4>

                  <div className="mt-1">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">
                      {c.code}
                    </span>
                  </div>
                </div>
              </div>


              {/* stats */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">

                {/* ผู้เรียน */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-500">
                    <Users size={18} />
                  </div>

                  <div>
                    <div className="font-semibold text-gray-800">
                      {c.learner_count || 0}
                    </div>
                    <div className="text-gray-500 text-xs">
                      ผู้เรียน
                    </div>
                  </div>
                </div>

                {/* ผู้สอน */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-500">
                    <GraduationCap size={18} />
                  </div>

                  <div>
                    <div className="font-semibold text-gray-800">
                      {c.user_count || 0}
                    </div>
                    <div className="text-gray-500 text-xs">
                      ผู้สอน/ผู้เกี่ยวข้อง
                    </div>
                  </div>
                </div>


                {/* เนื้อหา */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-500">
                    <FileText size={18} />
                  </div>

                  <div>
                    <div className="font-semibold text-gray-800">
                      {c.content_count || 0}
                    </div>
                    <div className="text-gray-500 text-xs">
                      เนื้อหา
                    </div>
                  </div>
                </div>


                {/* ชุดข้อสอบ */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-500">
                    <Zap size={18} />
                  </div>

                  <div>
                    <div className="font-semibold text-gray-800">
                      {c.test_count || 0}
                    </div>
                    <div className="text-gray-500 text-xs">
                      ชุดข้อสอบ
                    </div>
                  </div>
                </div>

              </div>


              {/* buttons */}
              <div className="grid grid-cols-2 gap-3 pt-2">

                <button
                  onClick={() => setContentsModal({
                    open: true,
                    courseId: c.id,
                    courseName: c.name
                  })}
                  className="border border-pink-400 text-pink-500 rounded-full py-2 text-sm hover:bg-pink-50 transition flex items-center justify-center gap-2"
                >
                  <PlusCircle size={16} />
                  เพิ่มเนื้อหา
                </button>


                <button
                  onClick={() => setTestsModal({
                    open: true,
                    courseId: c.id,
                    courseName: c.name
                  })}
                  className="bg-pink-500 hover:bg-pink-600 text-white rounded-full py-2 text-sm transition flex items-center justify-center gap-2"
                >
                  <PlusCircle size={16} />
                  เพิ่มชุดข้อสอบ
                </button>

              </div>


              {/* footer */}
              <div className="text-xs text-gray-500 pt-2">

                <div>
                  สร้างเมื่อ: {formatDateTime(c.created_at)}
                </div>

                <div className="flex items-center gap-2 text-pink-500 mt-1">
                  <span className="w-1.5 h-1.5 bg-pink-500 rounded-full" />
                  อัพเดตเมื่อ: {formatDateTime(c.updated_at || c.created_at)}
                </div>

              </div>


            </motion.div>

          ))}
        </motion.div>
      )}

      {/* Table View */}
      {view === "table" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: .35, delay: .1 }}
          className="bg-white rounded-xl shadow-md overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-[880px] w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
              <tr>
                <th className="py-2 px-3 w-12 text-left">#</th>
                <th className="py-2 px-3 text-left">ชื่อย่อ</th>
                <th className="py-2 px-3 text-left">ชื่อหลักสูตร</th>
                <th className="py-2 px-3 text-center">เนื้อหา</th>
                <th className="py-2 px-3 text-center">ชุดข้อสอบ</th>
                <th className="py-2 px-3 text-center">ผู้เรียน</th>
                <th className="py-2 px-3 text-center">ผู้สอน/ผู้เกี่ยวข้อง</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">สร้างเมื่อ</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">แก้ไขล่าสุด</th>
                <th className="py-2 px-3 w-40 text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-6 text-center"><Loader className="animate-spin" /></td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={9} className="py-6 text-center text-gray-500">– ไม่มีหลักสูตร –</td></tr>
              ) : list.map((c, i) => (
                <tr key={c.id} className="odd:bg-white even:bg-gray-50 whitespace-nowrap">
                  <td className="py-2 px-3">{i + 1}</td>
                  <td className="py-2 px-3">{c.code}</td>
                  <td className="py-2 px-3">{c.name}</td>
                  <td className="py-2 px-3 text-center">{c.content_count || 0}</td>
                  <td className="py-2 px-3 text-center">{c.test_count || 0}</td>
                  <td className="py-2 px-3 text-center">{c.learner_count || 0}</td>
                  <td className="py-2 px-3 text-center">{c.user_count || 0}</td>
                  <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(c.created_at)}</td>
                  <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(c.updated_at || c.created_at)}</td>
                  <td className="py-2 px-3 text-right space-x-2">
                    <button
                      onClick={() => setTestsModal({ open: true, courseId: c.id, courseName: c.name })}
                      className="text-primary hover:opacity-80"
                      title="จัดการชุดข้อสอบ"
                    >
                      <ClipboardList size={18} />
                    </button>
                    {/* หากอยากเปิดดูผู้สอน/ผู้เกี่ยวข้อง: */}
                    {/* <button
                      onClick={() => setUsersModal({ open: true, courseId: c.id, courseName: c.name })}
                      className="text-secondary hover:opacity-80"
                      title="ผู้สอน/ผู้เกี่ยวข้อง"
                    >
                      <Users size={18} />
                    </button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {/* Modals */}
      {testsModal.open && (
        <CourseTestsModal
          open={testsModal.open}
          courseId={testsModal.courseId}
          courseName={testsModal.courseName}
          onClose={() => setTestsModal({ open: false, courseId: null, courseName: "" })}
        />
      )}
      {contentsModal.open && <CourseContentModal {...contentsModal} onClose={() => { setContentsModal({ open: false, courseId: null, courseName: "" }); load(); }} />}
      {/* {usersModal.open && (
        <CourseUsersModal
          open={usersModal.open}
          courseId={usersModal.courseId}
          courseName={usersModal.courseName}
          onClose={() => setUsersModal({ open: false, courseId: null, courseName: "" })}
        />
      )} */}
    </motion.section>
  )
}
